import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createPassword, clearCreatePasswordError, logout } from '../contents/redux/actions/authActions';
import styles from './utilities/cssmodules/CreatePassword.module.css';
import { toast } from 'react-toastify';

import { VisibilityOutlined, VisibilityOffOutlined  } from "@mui/icons-material";
import lccLogo from "../../src/images/lcc_logo.png";

const CreatePassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  const { resetPasswordError, invalidResetPasswordTokenError } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
  // Log out if active session exists
  if (localStorage.getItem('isLoggedIn') === 'true') {
      dispatch(logout());
      toast.info('Logged out due to password reset session.');
    }

    dispatch(clearCreatePasswordError());
  }, [dispatch]);

  useEffect(() => {
    dispatch(clearCreatePasswordError());
  }, [dispatch]);

  // Password validation rules
  const passwordRules = {
    minLength: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: /[^\w]/.test(password), 
    // symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password), 
  };

  const passwordMeetsRules =
    passwordRules.minLength && passwordRules.uppercase && passwordRules.number && passwordRules.symbol;

  const doPasswordsMatch =
    password && confirmPassword && password === confirmPassword;

  // Combine errors
  const displayError = localError || resetPasswordError || invalidResetPasswordTokenError;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setLocalError('Invalid or missing token. Please check your link.');
      return;
    }

    if (!password || !confirmPassword) {
      setLocalError('Please fill out both fields.');
      return;
    }

    if (!passwordMeetsRules) {
      setLocalError(
        'Password must have at least 8 characters, one uppercase letter, one number and one symbol.'
      );
      return;
    }

    if (!doPasswordsMatch) {
      setLocalError('Passwords do not match.');
      return;
    }

    setLocalError('');
    setLoading(true);

    try {
      await dispatch(createPassword({ token, password, confirmPassword }, navigate));
    } catch (error) {
      // Error already handled in action via toast
    } finally {
      setLoading(false);
    }
  };

  // ✅ Hide form if token is missing
  if (!token) {
    return (
      <div className={styles.create_password_wrapper}>
        <h2>Create Your Password</h2>
        <p className={styles.error_message}>
          Invalid or missing token. Please check your password reset link.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.create_password_wrapper}>
      <div className={styles.action_component_header}>
        <div className={styles.action_component_header_logo}>
          <img src={lccLogo} alt="lcc logo" className={styles.logo_1} />
        </div>
        <div className={styles.action_component_header_text}>Liberia Chamber of Commerce</div>
      </div>


      <p className={styles.description}>
        To complete your account setup, please create a new password.  
        Your password must meet the security requirements listed below to ensure your account remains protected.
      </p>
      {displayError && <p className={styles.error_message}>{displayError}</p>}

      <form onSubmit={handleSubmit} className={styles.create_password_form}>
        <div className={styles.field_group}>
          <label htmlFor="password">Create a password</label>
          <div className={styles.password_input_wrapper}>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span onClick={() => setShowPassword(!showPassword)} className={styles.eye_button}>
              {showPassword ? <VisibilityOutlined /> : <VisibilityOffOutlined /> }
            </span>
          </div>
        </div>


        <div className={styles.field_group}>
          <label htmlFor="confirmPassword">Confirm password</label>
          <div className={styles.password_input_wrapper}>
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <span onClick={() => setShowConfirmPassword(!showConfirmPassword)} className={styles.eye_button}>
              {showConfirmPassword ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
            </span>
          </div>
          {confirmPassword && !doPasswordsMatch && (
            <p className={styles.error_message}>Passwords do not match.</p>
          )}
        </div>

        {/* <div className={styles.field_group}>
          <label htmlFor="confirmPassword">Confirm password</label>
          <input
            id="confirmPassword"
            type="password"
            placeholder="Re-enter your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <span type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className={styles.eye_button}>
            {showConfirmPassword ? <VisibilityOutlined /> : <VisibilityOffOutlined /> }
          </span>
          {confirmPassword && !doPasswordsMatch && (
            <p className={styles.error_message}>Passwords do not match.</p>
          )}
        </div> */}

        <div className={styles.validation_feedback}>
          <h4>Your password must contain:</h4>
          <p className={passwordRules.minLength ? styles.valid_rule : styles.invalid_rule}>
            At least 8 characters
          </p>
          <p className={passwordRules.uppercase ? styles.valid_rule : styles.invalid_rule}>
            At least one uppercase letter
          </p>
          <p className={passwordRules.number ? styles.valid_rule : styles.invalid_rule}>
            At least one number
          </p>
          <p className={passwordRules.symbol ? styles.valid_rule : styles.invalid_rule}>
            At least one symbol (!@#$%^&amp;*/, etc.)
          </p>
        </div>

        {passwordMeetsRules && doPasswordsMatch && (
          <button type="submit" disabled={loading} className={styles.submit_button}>
            {loading ? 'Creating...' : 'Create Password'}
          </button>
        )}
      </form>
    </div>
  );
};

export default CreatePassword;
